import type { BlogCtaId, BlogRouteId } from "./types";

export type BlogRouteMeta = {
  id: BlogRouteId;
  /** Short name: chips, filters, breadcrumbs. */
  label: string;
  /** H1 of the route page and heading in the route explorer. */
  title: string;
  description: string;
  /** Service page that goes deeper on the same piece (label with its article). */
  service: { label: string; to: string };
};

/* One route per piece of the Sistema Lucentia®, in the same order. */
export const BLOG_ROUTES: readonly BlogRouteMeta[] = [
  {
    id: "instagram",
    label: "Instagram",
    title: "Ruta Instagram: de perfil a reservas",
    description:
      "Perfil, bio, contenido y métricas para que Instagram te traiga clientes y no solo seguidores.",
    service: { label: "la Gestión Estratégica de Instagram", to: "/gestion-instagram" },
  },
  {
    id: "web",
    label: "Web",
    title: "Ruta Web: una página que trabaja por vos",
    description:
      "Estructura, textos y SEO local para que te encuentren en Google cuando te buscan y reserven sin dudar.",
    service: { label: "la Página Web Estratégica", to: "/pagina-web" },
  },
  {
    id: "reservas",
    label: "Reservas",
    title: "Ruta Reservas: de la consulta a la cita",
    description:
      "Sistemas de reserva online, WhatsApp y cómo quitar cada paso que separa el interés de la cita.",
    service: { label: "la Página Web Estratégica", to: "/pagina-web" },
  },
  {
    id: "automatizacion",
    label: "Automatización",
    title: "Ruta Automatización: menos tareas repetidas",
    description:
      "Recordatorios, respuestas y seguimientos automáticos para ganar tiempo sin perder cercanía.",
    service: { label: "el Sistema Lucentia®", to: "/metodo" },
  },
  {
    id: "optimizacion",
    label: "Optimización",
    title: "Ruta Optimización: medir y mejorar",
    description: "Qué métricas mirar cada mes, cómo leerlas y qué cambiar para que tu negocio siga creciendo.",
    service: { label: "la Asesoría Estratégica de Instagram", to: "/asesoria-instagram" },
  },
];

export const BLOG_ROUTE_BY_ID = Object.fromEntries(BLOG_ROUTES.map((route) => [route.id, route])) as Record<
  BlogRouteId,
  BlogRouteMeta
>;

export function blogRoutePath(id: BlogRouteId): string {
  return `/blog/ruta/${id}`;
}

export function blogPostPath(slug: string): string {
  return `/blog/${slug}`;
}

export type BlogCtaMeta = {
  title: string;
  text: string;
  button: string;
  /** Internal page; the free audit opens WhatsApp instead. */
  to?: string;
};

/** The closing call to action Jenifer picks for each article. */
export const BLOG_CTAS: Record<BlogCtaId, BlogCtaMeta> = {
  audit: {
    title: "¿Querés que lo apliquemos a tu negocio?",
    text: "Pedí tu Auditoría Estratégica sin coste por WhatsApp: revisamos tu presencia digital y te digo por dónde empezar.",
    button: "Auditoría gratuita",
  },
  resources: {
    title: "Seguí con las guías gratuitas",
    text: "Descargá las guías de Lucentia para aplicar esto paso a paso en tu negocio.",
    button: "Ver recursos gratuitos",
    to: "/recursos",
  },
  "gestion-instagram": {
    title: "¿Preferís que me ocupe yo de tu Instagram?",
    text: "Con la Gestión Estratégica de Instagram planifico, creo y publico tu contenido para que te traiga reservas.",
    button: "Ver Gestión de Instagram",
    to: "/gestion-instagram",
  },
  "pagina-web": {
    title: "Una web que convierte visitas en citas",
    text: "La Página Web Estratégica ordena tus servicios, te posiciona en Google y lleva directo a reservar.",
    button: "Ver Página Web Estratégica",
    to: "/pagina-web",
  },
  "asesoria-instagram": {
    title: "¿Querés hacerlo vos, con un plan claro?",
    text: "En la Asesoría Estratégica de Instagram revisamos tu cuenta en una sesión y te llevás un plan para aplicar.",
    button: "Ver Asesoría de Instagram",
    to: "/asesoria-instagram",
  },
  precios: {
    title: "Planes claros, sin letra pequeña",
    text: "Mirá qué incluye cada plan de Gestión de Instagram y los servicios que podés contratar por separado.",
    button: "Ver precios",
    to: "/precios",
  },
};

/** Labels the admin shows next to each call to action. */
export const BLOG_CTA_LABELS: Record<BlogCtaId, string> = {
  audit: "Auditoría gratuita (WhatsApp)",
  resources: "Recursos gratuitos",
  "gestion-instagram": "Servicio: Gestión de Instagram",
  "pagina-web": "Servicio: Página Web Estratégica",
  "asesoria-instagram": "Servicio: Asesoría de Instagram",
  precios: "Precios",
};
